/* Bearing Bot — minimal embed (bottom-left)
   Drop this into Assets/bearing-bot.js and include it in theme.liquid.
   Edit API_BASE_URL if needed. */

(() => {
  // --- CHANGED: Updated to API_BASE_URL ---
  // !!! IMPORTANT: This should be your *base* Cloud Run URL, *without* /assistant
  const API_BASE_URL = "https://bearing-assistant-service-1091843918701.us-central1.run.app";

  // --- NEW: Define specific endpoint URLs ---
  const ASSISTANT_URL = `${API_BASE_URL}/assistant`;
  const TOKEN_URL = `${API_BASE_URL}/auth/token`;

  // --- NEW: Variable to hold the session token ---
  let jwtToken = null;

  // guard: don’t double-inject
  if (window.__bearingBotMounted) return;
  window.__bearingBotMounted = true;

  // --- NEW: Function to get and cache the JWT ---
  async function getJwtToken() {
    // 1. If we already have a token, return it
    if (jwtToken) {
      return jwtToken;
    }

    // 2. If not, fetch a new one
    try {
      const resp = await fetch(TOKEN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!resp.ok) {
        throw new Error(`Token request failed with status ${resp.status}`);
      }
      const data = await resp.json();
      if (!data.access_token) {
        throw new Error("No access_token in response");
      }
      // 3. Cache and return the new token
      jwtToken = data.access_token;
      return jwtToken;
    } catch (err) {
      console.error("Failed to fetch auth token:", err);
      // Re-throw to be caught by the main submit handler
      throw new Error("Could not authenticate with the assistant service.");
    }
  }

  // --- helpers
  const el = (tag, attrs = {}, children = []) => {
    const node = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => {
      if (k === "class") node.className = v;
      else if (k === "html") node.innerHTML = v;
      else node.setAttribute(k, v);
    });
    (Array.isArray(children) ? children : [children]).filter(Boolean).forEach(c => node.appendChild(c));
    return node;
  };

  const addTyping = (wrap) => {
    const holder = el("div", { class: "msg bot" },
      el("div", { class: "typing", html: "<span></span><span></span><span></span>" })
    );
    wrap.appendChild(holder);
    wrap.scrollTop = wrap.scrollHeight;
    return holder;
  };

  // --- Shopify image lookup (client-side, no secrets)
  const IMG_CACHE = new Map();

  async function searchProductsFast(q, limit = 4) {
    // Predictive Search: find products by title/vendor/variant.sku
    const url =
      `${window.Shopify.routes.root}search/suggest.json` +
      `?q=${encodeURIComponent(q)}` +
      `&resources[type]=product` +
      `&resources[options][fields]=title,variants.sku,vendor` +
      `&resources[options][unavailable_products]=last` +
      `&resources[limit_scope]=all&resources[limit]=${limit}`;
    const r = await fetch(url, { credentials: "same-origin" });
    if (!r.ok) throw new Error("predictive search failed");
    const j = await r.json();
    return j?.resources?.results?.products || [];
  }

  function productHandleFromUrl(u) {
    try { const m = u.match(/\/products\/([^/?#]+)/); return m ? m[1] : null; } catch { return null; }
  }

  async function getProductJsonByHandle(handle) {
    const r = await fetch(`${window.Shopify.routes.root}products/${handle}.js`, { credentials: "same-origin" });
    if (!r.ok) throw new Error("product json failed");
    return r.json();
  }

  async function resolveImageForPart(queryText) {
    // cache by normalized query
    const key = (queryText || "").toLowerCase().trim();
    if (IMG_CACHE.has(key)) return IMG_CACHE.get(key);

    try {
      const results = await searchProductsFast(queryText, 4);
      for (const p of results) {
        // Prefer exact-ish title/sku hits if available
        const handle = productHandleFromUrl(p.url || "");
        if (!handle) continue;
        const pj = await getProductJsonByHandle(handle);
        const url = (pj?.images?.[0]) || pj?.featured_image || p?.image;
        if (url) { IMG_CACHE.set(key, url); return url; }
      }
    } catch (e) {
      console.warn("image lookup failed:", e);
    }
    IMG_CACHE.set(key, null);
    return null;
  }

  // Lazy-load thumbnails only when visible to avoid rate limits
  const _thumbIO = new IntersectionObserver((entries) => {
    entries.forEach(async (ent) => {
      if (!ent.isIntersecting) return;
      _thumbIO.unobserve(ent.target);
      const q = ent.target.dataset.query || "";
      const url = await resolveImageForPart(q);
      if (url) {
        const img = new Image();
        img.alt = q;
        img.loading = "lazy";
        img.decoding = "async";
        img.src = url;
        ent.target.innerHTML = "";
        ent.target.appendChild(img);
      } else {
        ent.target.classList.add("bbp-thumb--empty");
      }
    });
  }, { root: null, rootMargin: "200px 0px" });

  function mountThumb(el, query) {
    el.dataset.query = query;
    _thumbIO.observe(el);
  }


  // Turn URLs/emails into clickable links safely (no innerHTML use for main text)
  const linkify = (text) => {
    const frag = document.createDocumentFragment();
    const re = /(https?:\/\/[^\s)]+|\b[\w.-]+@[\w.-]+\.\w+\b)/g;
    let last = 0, m;
    while ((m = re.exec(text))) {
      if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));
      const url = m[0].includes("://") ? m[0] : `mailto:${m[0]}`;
      const a = el("a", { href: url, target: "_blank", rel: "noopener noreferrer" });
      a.appendChild(document.createTextNode(m[0]));
      frag.appendChild(a);
      last = re.lastIndex;
    }
    if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
    return frag;
  };

  // Render multi-line reply → paragraphs + real bullet list items
  const renderReply = (text) => {
    const frag = document.createDocumentFragment();
    const lines = text.split(/\n/);
    let ul = null;
    const flushList = () => { if (ul) { frag.appendChild(ul); ul = null; } };

    for (const raw of lines) {
      const line = raw.trimEnd();
      if (/^\s*[-•]\s+/.test(line)) {
        if (!ul) ul = el("ul", { class: "bbp-list" });
        const li = document.createElement("li");
        li.appendChild(linkify(line.replace(/^\s*[-•]\s+/, "")));
        ul.appendChild(li);
      } else if (line) {
        flushList();
        const p = document.createElement("p");
        p.appendChild(linkify(line));
        frag.appendChild(p);
      } else {
        flushList(); // blank line = paragraph break
      }
    }
    flushList();
    return frag;
  };

  const addUserMsg = (wrap, text) => {
    const div = el("div", { class: "msg user" });
    div.textContent = text;
    wrap.appendChild(div);
    wrap.scrollTop = wrap.scrollHeight;
    return div;
  };

  const addBotMsg = (wrap, text) => {
    const div = el("div", { class: "msg bot" });
    div.appendChild(renderReply(text));
    wrap.appendChild(div);
    wrap.scrollTop = wrap.scrollHeight;
    return div;
  };

  const fmtNum = (v) => (v == null ? "—" : String(Number(v)));
  const addMatches = (wrap, arr) => {
    if (!arr || arr.length === 0) {
      return; // Exit if there are no matches
    }

    // A container for all the results for better structure and styling
    const resultsContainer = el('div', { class: 'bbp-results-container' });

    // Group products by their dimension string
    const groupedByDim = new Map();
    arr.slice(0, 8).forEach(m => {
      // --- NOTE: This function's internals are UNCHANGED ---
      // It still expects { d_mm, od_mm, b_mm }
      const dimKey = `${fmtNum(m.d_mm)} × ${fmtNum(m.od_mm)} × ${fmtNum(m.b_mm)} mm`;
      if (!groupedByDim.has(dimKey)) {
        groupedByDim.set(dimKey, []);
      }
      groupedByDim.get(dimKey).push(m);
    });

    // Render each group into the container
    groupedByDim.forEach((products, dimKey) => {
      const group = el('div', { class: 'bbp-match-group' });
      group.appendChild(el('div', { class: 'bbp-group-header', html: dimKey }));

      products.forEach(m => {
        // --- NOTE: This is UNCHANGED ---
        // It still expects { designation_full }
        const q = (m.designation_full || '(unnamed)').trim();

        const link = el('a', { class: 'bbp-part-link', href: `/search?q=${encodeURIComponent(q)}`, target: '_blank', rel: 'noopener' });
        link.appendChild(document.createTextNode(q));

        (async () => {
          try {
            const results = await searchProductsFast(q, 1);
            if (results && results.length > 0 && results[0].url) {
              link.href = results[0].url;
            }
          } catch (e) { console.warn("Direct link lookup failed:", e); }
        })();

        const copyBtnText = el('span', { html: 'Copy' });
        const copyBtn = el('button', { type: 'button', class: 'bbp-action-btn bbp-copy', 'aria-label': `Copy ${q}` }, [copyBtnText]);
        copyBtn.addEventListener('click', () => {
          navigator.clipboard.writeText(q).then(() => {
            copyBtnText.textContent = 'Copied!';
            setTimeout(() => { copyBtnText.textContent = 'Copy'; }, 1500);
          }).catch(err => console.error("Copy failed", err));
        });

        const quoteBtn = el('a', { class: 'bbp-action-btn bbp-quote', href: '/pages/contact-us', target: '_blank', rel: 'noopener' }, [el('span', { html: 'Request Quote' })]);

        const row = el('div', { class: 'bbp-product-row' }, [
          el('div', { class: 'bbp-thumb' }),
          el('div', { class: 'bbp-info' }, [
            link,
            el('div', { class: 'bbp-actions' }, [copyBtn, quoteBtn])
          ])
        ]);

        group.appendChild(row);
        mountThumb(row.querySelector('.bbp-thumb'), q);
      });
      resultsContainer.appendChild(group);
    });

    // Append the single container with all results to the chat log
    wrap.appendChild(resultsContainer);
    wrap.scrollTop = wrap.scrollHeight;
  };


  const onOutsideClick = (e) => {
    if (!panel.classList.contains("bearing-bot--open")) return;
    const t = e.target;
    if (panel.contains(t) || launcher.contains(t)) return; // ignore clicks inside panel or on launcher
    close();
  };
  document.addEventListener("pointerdown", onOutsideClick, true);


  // --- UI
  const launcher = el("button", { id: "bearing-bot-launcher", "aria-label": "Open bearing assistant", type: "button" });
  launcher.innerHTML = `
  <span style="display: flex; align-items: center; gap: 6px; font-family: Arial, sans-serif; font-size: 14px; font-weight: 600;">
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" 
         viewBox="0 0 24 24" fill="none" stroke="currentColor" 
         stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <rect x="3" y="11" width="18" height="10" rx="2" ry="2"></rect>
      <circle cx="7.5" cy="16" r="1"></circle>
      <circle cx="16.5" cy="16" r="1"></circle>
      <path d="M12 2v4"></path>
      <path d="M9 7h6"></path>
    </svg>
    <span style="margin-top: 3px">AI Assistant</span>
  </span>
`;


  const header = el("div", { class: "bbp-header" }, [
    el("div", { class: "bbp-title" }, [
      el("span", { class: "bbp-dot" }),
      el("span", { class: "bbp-title-text" }),
    ]),
    el("button", { class: "bbp-close", type: "button", "aria-label": "Close" }, [
      el("span", { html: "&times;" })
    ])
  ]);
  header.querySelector(".bbp-title-text").textContent = "Bearing assistant";

  // Accessible transcript (screen readers announce new messages in order)
  const log = el("div", {
    id: "bearing-bot-log",
    role: "log",
    "aria-live": "polite",
    "aria-relevant": "additions text"
  });

  const form = el("form", { class: "bbp-form" }, [
    el("input", {
      id: "bearing-bot-input",
      type: "text",
      placeholder: `Try "6206 bearing" or "20x47x14 mm"`,
      autocomplete: "off",
      "aria-label": "Ask the bearing assistant"
    }),
    // New microphone button
    el("button", { 
      type: "button", 
      id: "bearing-bot-voice-btn",
      "aria-label": "Use voice input",
      html: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" y1="19" x2="12" y2="22"></line></svg>`
    }),
    el("button", { type: "submit" }, [document.createTextNode("Send")])
  ]);

  const panel = el("section", { id: "bearing-bot-panel", role: "dialog", "aria-modal": "true", "aria-label": "Bearing assistant" }, [
    header, log, form
  ]);

  document.body.appendChild(launcher);
  document.body.appendChild(panel);

  // --- open/close
  const open = () => { panel.classList.add("bearing-bot--open"); input.focus(); };
  const close = () => { panel.classList.remove("bearing-bot--open"); launcher.focus(); };
  launcher.addEventListener("click", open);
  header.querySelector(".bbp-close").addEventListener("click", close);
  document.addEventListener("keydown", (e) => { if (e.key === "Escape" && panel.classList.contains("bearing-bot--open")) close(); });

  // seed welcome
  addBotMsg(log, 
  "Hi there,\n\n" +
  "I'm an AI Assistant developed by SantEnt.\n\n" +
  "My capabilities include:\n" +
  "- Dimension Search - \"Which bearings have the dimensions 20 x 47 x 14 mm?\"\n" +
  "- Technical Assistance - \"How should you mount bearings?\"\n\n" +
  "PS - I'm constantly learning new things, and you are requested to revalidate the information that I share.");


  // --- send flow
  const input = form.querySelector("#bearing-bot-input");
  const submitBtn = form.querySelector('button[type="submit"]');

  // --- NEW: VOICE RECOGNITION LOGIC ---
  const voiceBtn = form.querySelector("#bearing-bot-voice-btn");
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  let recognition;

  // 1. Check if the browser supports the Web Speech API
  if (SpeechRecognition) {
    recognition = new SpeechRecognition();
    recognition.continuous = false; // Stop listening after the first phrase
    recognition.lang = 'en-US';

    // 2. Handle the result
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      input.value = transcript; // Put the transcribed text in the input field
      
      // Automatically submit the form
      form.requestSubmit ? form.requestSubmit(submitBtn) : form.dispatchEvent(new Event('submit', {cancelable: true}));
    };

    // 3. Handle errors (e.g., microphone permission denied)
    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      voiceBtn.classList.remove("listening");
    };
    
    // 4. Reset the button style when listening ends
    recognition.onend = () => {
      voiceBtn.classList.remove("listening");
    };

    // 5. Start listening when the microphone button is clicked
    voiceBtn.addEventListener("click", () => {
      voiceBtn.classList.add("listening");
      recognition.start();
    });

  } else {
    // If not supported, hide the button so it doesn't cause confusion
    voiceBtn.style.display = "none";
    console.warn("Speech Recognition API not supported in this browser.");
  }
  // --- END VOICE RECOGNITION LOGIC ---


  const setBusy = (busy) => {
    submitBtn.disabled = busy;
    input.disabled = busy;
  };

  // --- MODIFIED: Form submit handler ---
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const q = (input.value || "").trim();
    if (!q) return;
    addUserMsg(log, q);
    input.value = "";

    const typing = addTyping(log);
    setBusy(true);

    try {
      // --- NEW: Get the JWT token first ---
      const token = await getJwtToken();

      // --- MODIFIED: Use ASSISTANT_URL and add Authorization header ---
      const resp = await fetch(ASSISTANT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}` // <-- Send the token
        },
        body: JSON.stringify({ message: q })
      });
      // --- END MODIFICATIONS ---

      // Defensive: if gateway or HTML error, throw
      const ct = resp.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        // --- NEW: Handle 401 Unauthorized specifically ---
        if (resp.status === 401) {
            jwtToken = null; // Token is invalid or expired, clear it
            throw new Error("Authentication failed. Please reload.");
        }
        // --- END NEW ---
        throw new Error(`Unexpected response (${resp.status})`);
      }
      
      // --- CHANGED: Replaced response parsing logic to handle the new API structure ---
      const data = await resp.json();
      typing.remove();

      let hasReply = false;

      // 1. Handle Chat Intent
      if (data.intent === 'chat' && data.chat_response && data.chat_response.response) {
        addBotMsg(log, data.chat_response.response);
        hasReply = true;
      }

      // 2. Handle Search Intent
      if (data.intent === 'search' && data.search_response && Array.isArray(data.search_response.results)) {
        if (data.search_response.results.length > 0) {
          // Adapt the new schema to what the 'addMatches' function expects
          // This avoids changing 'addMatches' itself, preserving frontend functionality
          const adaptedMatches = data.search_response.results.map(bearing => ({
            d_mm: bearing.inner_diameter_mm,   // Map inner_diameter_mm -> d_mm
            od_mm: bearing.outer_diameter_mm,  // Map outer_diameter_mm -> od_mm
            b_mm: bearing.width_mm,            // Map width_mm -> b_mm
            designation_full: bearing.part_number // Map part_number -> designation_full
          }));
          
          addMatches(log, adaptedMatches);
          hasReply = true; // Showing matches counts as a reply
        }
      }

      // 3. Fallback (e.g., search found no results, or API returned empty/unexpected)
      if (!hasReply) {
         // If search intent was triggered but found 0 results, add a helpful message.
         if (data.intent === 'search') {
           addBotMsg(log, "I couldn't find any bearings matching those criteria. Please try different dimensions.");
         } else {
           // Generic fallback, similar to the old one.
           addBotMsg(log, "I couldn't find anything specific for that. Please try rephrasing your question.");
         }
      }
      // --- END OF CHANGES ---

    } catch (err) {
      typing.remove();
      // --- MODIFIED: Show a more specific error for auth failures ---
      if (err.message.includes("Authentication failed")) {
         addBotMsg(log, "Sorry, your session expired. Please reload the page and try again.");
      } else if (err.message.includes("Could not authenticate")) {
         addBotMsg(log, "Sorry, I couldn't connect to the assistant. Please reload the page.");
      } else {
         addBotMsg(log, "Sorry, I couldn’t reach the bearing service. Please try again.");
      }
      // --- END MODIFIED ---

      // Surface details in console for debugging
      console.warn("bearing-bot error", err);
    } finally {
      setBusy(false);
      log.scrollTop = log.scrollHeight;
    }
  });
})();